import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { 
  Gift, Users, DollarSign, TrendingUp, Search, 
  ChevronLeft, ChevronRight, Trophy, RefreshCw
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentReferrals() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    setLoading(true);
    try {
      const [statsRes, referralsRes] = await Promise.all([
        axios.get(`${API}/agent/referrals/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/referrals`, {
          params: { page, status: statusFilter || undefined },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setReferrals(referralsRes.data.referrals);
      setTotalPages(referralsRes.data.pages);
    } catch (error) {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "converted":
        return <Badge className="bg-green-100 text-green-800">Converted</Badge>;
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <AgentLayout title="Referral Management">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              Referral Program
            </h1>
            <p className="text-muted-foreground">Track and manage customer referrals</p>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.total_referrals}</p>
                    <p className="text-sm text-muted-foreground">Total Referrals</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.successful_referrals}</p>
                    <p className="text-sm text-muted-foreground">Converted</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Gift className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">${stats.total_rewards_paid?.toFixed(2) || "0.00"}</p>
                    <p className="text-sm text-muted-foreground">Rewards Paid</p>
                  </div>
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top Referrers */}
        {stats?.top_referrers?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Top Referrers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.top_referrers.map((ref, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-amber-100 text-amber-800' :
                        idx === 1 ? 'bg-gray-200 text-gray-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium">{ref.user?.full_name || "User"}</p>
                        <p className="text-sm text-muted-foreground">{ref.user?.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{ref.count}</p>
                      <p className="text-xs text-muted-foreground">referrals</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          <Button
            variant={statusFilter === "" ? "default" : "outline"}
            size="sm"
            onClick={() => { setStatusFilter(""); setPage(1); }}
          >
            All
          </Button>
          <Button
            variant={statusFilter === "pending" ? "default" : "outline"}
            size="sm"
            onClick={() => { setStatusFilter("pending"); setPage(1); }}
          >
            Pending
          </Button>
          <Button
            variant={statusFilter === "converted" ? "default" : "outline"}
            size="sm"
            onClick={() => { setStatusFilter("converted"); setPage(1); }}
          >
            Converted
          </Button>
        </div>

        {/* Referrals Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : referrals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Gift className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No referrals found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium">Referrer</th>
                      <th className="pb-3 font-medium">Referred User</th>
                      <th className="pb-3 font-medium">Code Used</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {referrals.map((ref) => (
                      <tr key={ref.referral_id} className="hover:bg-muted/50">
                        <td className="py-3">
                          <p className="font-medium">{ref.referrer?.full_name || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground">{ref.referrer?.email}</p>
                        </td>
                        <td className="py-3">
                          <p className="font-medium">{ref.referred_user?.full_name || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground">{ref.referred_user?.email}</p>
                        </td>
                        <td className="py-3">
                          <code className="bg-muted px-2 py-1 rounded text-sm">
                            {ref.referral_code_used}
                          </code>
                        </td>
                        <td className="py-3">
                          {getStatusBadge(ref.status)}
                        </td>
                        <td className="py-3 text-sm text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-2 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AgentLayout>
  );
}
